import React, { useState, useEffect } from 'react';
import { Award, X, PartyPopper, Calendar } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

const AniversarioWidget = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);

    // Check dismiss state
    useEffect(() => {
        const today = new Date().toDateString();
        const key = `widget_dismiss_aniversario_${today}`;
        if (localStorage.getItem(key)) setDismissed(true);
    }, []);

    useEffect(() => {
        fetchAniversario();
    }, []);

    const fetchAniversario = async () => {
        try {
            const response = await axiosInstance.get('/api/empleados/mi-aniversario');
            if (response.data.success && response.data.aniversario) {
                setData(response.data.aniversario);
            }
        } catch (error) {
            console.log('Aniversario API not available yet');
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = () => {
        if (window.confirm('¿Deseas ocultar este widget por hoy?')) {
            const today = new Date().toDateString();
            localStorage.setItem(`widget_dismiss_aniversario_${today}`, 'true');
            setDismissed(true);
        }
    };

    if (loading || !data || dismissed) return null;

    const { anios_cumplidos, dias_restantes, es_hoy } = data;

    // CSS confetti keyframes (inline for self-contained component)
    const confettiStyles = es_hoy ? `
        @keyframes confetti-fall {
            0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
            100% { transform: translateY(200px) rotate(720deg); opacity: 0; }
        }
        @keyframes confetti-shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            75% { transform: translateX(5px); }
        }
    ` : '';

    const confettiColors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FF69B4', '#DDA0DD'];

    return (
        <>
            {confettiStyles && <style>{confettiStyles}</style>}
            <section className="relative rounded-2xl overflow-hidden shadow-sm group border border-amber-200">
                {/* Dismiss button */}
                <button
                    onClick={handleDismiss}
                    className="absolute top-3 right-3 z-20 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm text-gray-400 hover:text-gray-600 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                    title="Ocultar por hoy"
                >
                    <X size={14} />
                </button>

                {/* Gold/Purple gradient background */}
                <div className="bg-gradient-to-br from-amber-400 via-yellow-500 to-purple-600 p-5 text-white relative overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-400/20 rounded-full blur-2xl -ml-8 -mb-8"></div>

                    {/* Confetti particles (only on anniversary day) */}
                    {es_hoy && (
                        <div className="absolute inset-0 pointer-events-none overflow-hidden">
                            {Array.from({ length: 20 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="absolute"
                                    style={{
                                        left: `${Math.random() * 100}%`,
                                        top: `-${Math.random() * 20}px`,
                                        width: `${4 + Math.random() * 6}px`,
                                        height: `${4 + Math.random() * 6}px`,
                                        backgroundColor: confettiColors[i % confettiColors.length],
                                        borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                                        animation: `confetti-fall ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 3}s infinite`,
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                {es_hoy ? (
                                    <span className="text-2xl animate-bounce">🎉</span>
                                ) : (
                                    <Award size={24} />
                                )}
                            </div>
                            <div>
                                <h3 className="font-bold text-lg">
                                    {es_hoy ? '¡Feliz Aniversario!' : 'Aniversario Laboral'}
                                </h3>
                                <p className="text-white/80 text-sm">
                                    {es_hoy ? 'Un día muy especial' : 'Se acerca tu día'}
                                </p>
                            </div>
                        </div>

                        {es_hoy ? (
                            /* Anniversary day - celebration mode */
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                                <p className="text-3xl mb-2">🎂</p>
                                <p className="text-2xl font-black mb-1">
                                    ¡{anios_cumplidos} {anios_cumplidos === 1 ? 'año' : 'años'} en SURA!
                                </p>
                                <p className="text-white/90 text-sm">
                                    Gracias por tu dedicación y compromiso. ¡Celebremos juntos!
                                </p>
                            </div>
                        ) : dias_restantes > 0 ? (
                            /* Countdown mode */
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-white/80 text-xs uppercase tracking-wide font-medium mb-1">
                                            Próximo aniversario
                                        </p>
                                        <p className="text-white text-sm">
                                            Cumplirás <span className="font-bold text-lg">{anios_cumplidos}</span> {anios_cumplidos === 1 ? 'año' : 'años'} en SURA
                                        </p>
                                    </div>
                                    <div className="text-center bg-white/20 rounded-xl px-4 py-2.5">
                                        <p className="text-3xl font-black leading-none">{dias_restantes}</p>
                                        <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold mt-0.5">
                                            {dias_restantes === 1 ? 'día' : 'días'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Post-anniversary (up to 3 days after) */
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                                <p className="text-xl font-bold mb-1">
                                    🎉 ¡Cumpliste {anios_cumplidos} {anios_cumplidos === 1 ? 'año' : 'años'}!
                                </p>
                                <p className="text-white/90 text-sm">
                                    Gracias por ser parte de SURA
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
};

export default AniversarioWidget;
